import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Instructions from './Instructions';
import ToggleSwitch from './ToggleSwitch';
import CreditsBar from './CreditsBar';
import BrandNameInput from './BrandNameInput';
import OfflineQueueStatus from './OfflineQueueStatus';
import UploadsTray from './UploadsTray';
import { enqueueUpload } from '../lib/uploadQueue';
import { getPreferences, savePreferences } from '../lib/preferences';
import { getSession, getCredits, deductCredit } from '../lib/auth';
import { downloadUserCsv } from '../lib/wasabi';

const MAX_RECORDING_SECONDS = 300;
const PERMANENT_WATERMARK   = 'Powered by ShipAssure.in';

const ShipmentRecorder = () => {
  const prefs = getPreferences();
  const [user, setUser]                         = useState(null);
  const [isRecording, setIsRecording]           = useState(false);
  const [startAwb, setStartAwb]                 = useState('');
  const [stopAwb, setStopAwb]                   = useState('');
  const [recordingTime, setRecordingTime]       = useState(0);
  const [error, setError]                       = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [downloadToPC, setDownloadToPCState]    = useState(prefs.downloadToPC);
  const [uploadToCloud, setUploadToCloudState]  = useState(prefs.uploadToCloud);
  const [brandName, setBrandNameState]          = useState(prefs.brandName || 'ShipAssure.in');
  const [cameraOn, setCameraOnState]            = useState(prefs.cameraOn !== false);
  const [credits, setCredits]                   = useState(0);
  const [savedNotif, setSavedNotif]             = useState(null); // only for local PC save
  const [csvDownloading, setCsvDownloading]     = useState(false);

  const videoRef         = useRef(null);
  const canvasRef        = useRef(null);
  const startInputRef    = useRef(null);
  const stopInputRef     = useRef(null);
  const timerRef         = useRef(null);
  const requestRef       = useRef(null);
  const chunksRef        = useRef([]);
  const mediaRecorderRef = useRef(null);
  const streamRef        = useRef(null);
  const isRecordingRef   = useRef(false);
  const recordingTimeRef = useRef(0);
  const notifTimerRef    = useRef(null);
  const downloadToPCRef  = useRef(downloadToPC);
  const uploadToCloudRef = useRef(uploadToCloud);
  const brandNameRef     = useRef(brandName);
  const startAwbRef      = useRef(startAwb);
  const userRef          = useRef(user);

  useEffect(() => { downloadToPCRef.current  = downloadToPC;  }, [downloadToPC]);
  useEffect(() => { uploadToCloudRef.current = uploadToCloud; }, [uploadToCloud]);
  useEffect(() => { brandNameRef.current     = brandName;     }, [brandName]);
  useEffect(() => { startAwbRef.current      = startAwb;      }, [startAwb]);
  useEffect(() => { userRef.current          = user;          }, [user]);

  useEffect(() => {
    const load = async () => {
      const { data } = await getSession();
      const u = data?.session?.user || null;
      setUser(u);
      if (u) setCredits(await getCredits(u.id));
    };
    load();
  }, []);

  const refreshCredits = useCallback(async () => {
    const u = userRef.current;
    if (u?.id) setCredits(await getCredits(u.id));
  }, []);

  const savePref = useCallback((patch) => {
    savePreferences({ ...getPreferences(), ...patch });
  }, []);

  const setDownloadToPC  = (val) => { setDownloadToPCState(val);  savePref({ downloadToPC: val }); };
  const setUploadToCloud = (val) => { setUploadToCloudState(val); savePref({ uploadToCloud: val }); };
  const setBrandName     = (val) => { setBrandNameState(val);     savePref({ brandName: val }); };

  const formatDate = (date) => {
    const p = (n) => String(n).padStart(2, '0');
    return `${p(date.getDate())}-${p(date.getMonth()+1)}-${date.getFullYear()} ${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
  };

  const speakMessage = (msg) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(new SpeechSynthesisUtterance(msg));
    }
  };

  const drawFrame = useCallback(() => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width  = video.videoWidth  || 1280;
      canvas.height = video.videoHeight || 720;
    }
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const line1 = `${formatDate(new Date())} | ${brandNameRef.current}`;
    const line2 = PERMANENT_WATERMARK;
    ctx.font = 'bold 18px monospace';
    const maxW  = Math.max(ctx.measureText(line1).width, ctx.measureText(line2).width);
    const lineH = 24, padX = 12, padY = 8;
    const boxH  = lineH * 2 + padY * 2;
    const boxY  = 8;
    ctx.fillStyle = 'rgba(0,0,0,0.60)';
    ctx.fillRect(8, boxY, maxW + padX * 2, boxH);
    ctx.fillStyle = 'white';
    ctx.fillText(line1, 8 + padX, boxY + padY + lineH - 4);
    ctx.fillStyle = '#FFD700';
    ctx.fillText(line2, 8 + padX, boxY + padY + lineH * 2 - 4);
    requestRef.current = requestAnimationFrame(drawFrame);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const ms = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 }, audio: false,
      });
      streamRef.current = ms;
      if (videoRef.current) {
        videoRef.current.srcObject = ms;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          cancelAnimationFrame(requestRef.current);
          requestRef.current = requestAnimationFrame(drawFrame);
        };
      }
    } catch { setError('Camera access denied. Please allow camera permissions.'); }
  }, [drawFrame]);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(requestRef.current);
    requestRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const toggleCamera = () => {
    if (isRecordingRef.current) return;
    const next = !cameraOn;
    setCameraOnState(next);
    savePref({ cameraOn: next });
    next ? startCamera() : stopCamera();
  };

  useEffect(() => {
    if (cameraOn) startCamera();
    setTimeout(() => startInputRef.current?.focus(), 300);
    return () => {
      cancelAnimationFrame(requestRef.current);
      stopCamera();
      clearInterval(timerRef.current);
      clearTimeout(notifTimerRef.current);
    };
  }, []);

  // ── Fire-and-forget: save locally and/or enqueue cloud upload ───────────────
  const handleSaveVideo = useCallback((chunks, awb) => {
    const blob     = new Blob(chunks, { type: 'video/webm' });
    const filename = `${awb}.webm`;
    const dlPC     = downloadToPCRef.current;
    const ulCloud  = uploadToCloudRef.current;
    const userId   = userRef.current?.id;

    if (!dlPC && !ulCloud) { setError('Enable at least one storage option.'); return; }

    // Local download — instant, no blocking
    if (dlPC) {
      const url = URL.createObjectURL(blob);
      const a   = Object.assign(document.createElement('a'), { href: url, download: filename });
      a.click();
      URL.revokeObjectURL(url);
      if (!ulCloud) {
        setSavedNotif({ filename });
        clearTimeout(notifTimerRef.current);
        notifTimerRef.current = setTimeout(() => setSavedNotif(null), 4000);
      }
    }

    // Cloud upload — non-blocking background queue
    if (ulCloud) {
      enqueueUpload(blob, filename, userId, awb);
    }
  }, []);

  const doStop = useCallback((awb) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || !isRecordingRef.current) return;

    clearInterval(timerRef.current);
    timerRef.current       = null;
    isRecordingRef.current = false;
    speakMessage('Recording Stopped');

    recorder.onstop = () => {
      // ✅ Immediately reset UI — user can start next recording right away
      setIsRecording(false);
      setStartAwb('');
      setStopAwb('');
      setRecordingTime(0);
      recordingTimeRef.current = 0;

      // Fire-and-forget save/upload
      handleSaveVideo(chunksRef.current, awb);

      // Refresh credits in background
      refreshCredits();

      chunksRef.current    = [];
      mediaRecorderRef.current = null;
      setTimeout(() => startInputRef.current?.focus(), 150);
    };

    try { recorder.stop(); } catch { /* already stopped */ }
  }, [handleSaveVideo, refreshCredits]);

  const startRecording = async (e) => {
    e.preventDefault();
    const awb = startAwb.trim();
    if (!awb)                                                    return setError('Please scan an AWB number.');
    if (!canvasRef.current)                                      return setError('Camera not ready. Please enable camera.');
    if (!downloadToPCRef.current && !uploadToCloudRef.current)   return setError('Enable at least one storage option.');
    if (credits <= 0)                                            return setError('No credits remaining. Please recharge.');

    const ok = await deductCredit(userRef.current?.id);
    if (!ok) return setError('No credits remaining.');
    await refreshCredits();
    setSavedNotif(null);

    try {
      setError('');
      chunksRef.current        = [];
      recordingTimeRef.current = 0;
      const canvas = canvasRef.current;
      if (!canvas.width) { canvas.width = 1280; canvas.height = 720; }

      const recorder = new MediaRecorder(canvas.captureStream(30), { mimeType: 'video/webm;codecs=vp8' });
      recorder.ondataavailable = (ev) => { if (ev.data?.size > 0) chunksRef.current.push(ev.data); };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      isRecordingRef.current   = true;
      setIsRecording(true);

      timerRef.current = setInterval(() => {
        recordingTimeRef.current += 1;
        setRecordingTime(recordingTimeRef.current);
        if (recordingTimeRef.current >= MAX_RECORDING_SECONDS) {
          doStop(startAwbRef.current);
          setError('Max 5 minutes reached — recording auto-saved.');
        }
      }, 1000);

      speakMessage('Recording Started');
      setTimeout(() => stopInputRef.current?.focus(), 100);
    } catch (err) { setError(`Recording error: ${err.message}`); }
  };

  const stopRecording = (e) => {
    e.preventDefault();
    if (stopAwb !== startAwb) {
      setError('AWB mismatch! Scan the same AWB used to start.');
      setStopAwb('');
      return;
    }
    doStop(stopAwb);
  };

  const handleDownloadCsv = async () => {
    const userId = userRef.current?.id;
    if (!userId) return setError('You must be logged in to download the CSV.');
    setCsvDownloading(true);
    try {
      await downloadUserCsv(userId);
    } catch {
      setError('No records found yet. Upload at least one video first.');
    } finally {
      setCsvDownloading(false);
    }
  };

  const timeLeft    = MAX_RECORDING_SECONDS - recordingTime;
  const timeWarning = isRecording && timeLeft <= 60;

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4">
      <div className="max-w-6xl mx-auto">

        {/* ── Header ── */}
        <motion.div
          className="bg-blue-600 text-white py-4 px-6 rounded-t-lg shadow-lg flex justify-between items-center"
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-bold">ShipAssure</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadCsv}
              disabled={csvDownloading}
              title="Download video log as CSV"
              className="bg-green-500 hover:bg-green-600 disabled:opacity-60 px-3 py-2 rounded flex items-center gap-2 transition-colors text-sm font-semibold"
            >
              <SafeIcon
                icon={csvDownloading ? FiIcons.FiLoader : FiIcons.FiDownload}
                className={csvDownloading ? 'animate-spin' : ''}
              />
              {csvDownloading ? 'Downloading…' : 'Download CSV'}
            </button>
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="bg-blue-500 hover:bg-blue-700 px-4 py-2 rounded flex items-center gap-2 transition-colors"
            >
              <SafeIcon icon={showInstructions ? FiIcons.FiX : FiIcons.FiHelpCircle} />
              {showInstructions ? 'Hide' : 'Help'}
            </button>
          </div>
        </motion.div>

        {/* ── Body ── */}
        <div className="bg-white rounded-b-lg shadow-lg p-6">
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4 flex justify-between items-center text-sm">
              <span>{error}</span>
              <button onClick={() => setError('')} className="ml-3 flex-shrink-0">
                <SafeIcon icon={FiIcons.FiX} />
              </button>
            </div>
          )}

          <OfflineQueueStatus />

          {/* Local save notification */}
          <AnimatePresence>
            {savedNotif && (
              <motion.div
                key="saved-notif"
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                className="mb-4 rounded-xl px-4 py-3 flex items-center gap-3 border bg-green-50 border-green-200 text-green-800"
              >
                <SafeIcon icon={FiIcons.FiCheckCircle} className="flex-shrink-0 text-lg" />
                <p className="text-sm font-semibold flex-1">💾 Saved to PC: {savedNotif.filename}</p>
                <button onClick={() => setSavedNotif(null)} className="opacity-60 hover:opacity-100">
                  <SafeIcon icon={FiIcons.FiX} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {showInstructions ? <Instructions /> : (
            <div className="flex flex-col md:flex-row gap-6">

              {/* ── Video preview ── */}
              <div className="md:w-3/4 flex flex-col">
                <div className="relative bg-black rounded-lg overflow-hidden aspect-video shadow-inner">
                  {cameraOn ? (
                    <>
                      <video ref={videoRef} className="hidden" autoPlay playsInline muted />
                      <canvas ref={canvasRef} className="w-full h-full object-cover" style={{ display: 'block' }} />
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 min-h-[200px]">
                      <SafeIcon icon={FiIcons.FiVideoOff} className="text-5xl mb-3 opacity-40" />
                      <p className="text-sm opacity-60">Camera is OFF</p>
                    </div>
                  )}
                  {isRecording && (
                    <div className="absolute top-4 right-4 flex items-center bg-red-600 text-white px-3 py-1 rounded-full animate-pulse z-10">
                      <div className="w-2 h-2 rounded-full bg-white mr-2" />
                      <span className="text-xs font-bold uppercase tracking-wider">REC</span>
                    </div>
                  )}
                  {timeWarning && (
                    <div className="absolute top-4 left-4 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold z-10">
                      ⏱ {formatTime(timeLeft)} left
                    </div>
                  )}
                </div>

                <div className="mt-1.5 flex items-center gap-1.5 px-1">
                  <SafeIcon icon={FiIcons.FiShield} className="text-yellow-500 text-xs flex-shrink-0" />
                  <p className="text-xs text-gray-400">
                    Protected & <span className="font-semibold text-yellow-600">{PERMANENT_WATERMARK}</span>
                  </p>
                </div>
              </div>

              {/* ── Controls ── */}
              <div className="md:w-1/4 space-y-3">
                <CreditsBar credits={credits} />

                <form onSubmit={startRecording} className="space-y-1">
                  <label className="text-sm font-bold text-gray-700 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Scan to START
                  </label>
                  <div className="flex">
                    <input ref={startInputRef} type="text" value={startAwb}
                      onChange={(e) => setStartAwb(e.target.value)}
                      disabled={isRecording || credits <= 0}
                      className="w-full border border-gray-300 p-2 rounded-l focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100 text-sm"
                      placeholder="Scan / type AWB" />
                    <button type="submit" disabled={isRecording || credits <= 0}
                      className="bg-blue-600 text-white px-3 rounded-r hover:bg-blue-700 disabled:opacity-50 transition-colors">
                      <SafeIcon icon={FiIcons.FiPlay} />
                    </button>
                  </div>
                </form>

                <form onSubmit={stopRecording} className="space-y-1">
                  <label className="text-sm font-bold text-gray-700 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Scan to STOP
                  </label>
                  <div className="flex">
                    <input ref={stopInputRef} type="text" value={stopAwb}
                      onChange={(e) => setStopAwb(e.target.value)}
                      disabled={!isRecording}
                      className="w-full border border-gray-300 p-2 rounded-l focus:ring-2 focus:ring-red-500 outline-none disabled:bg-gray-100 text-sm"
                      placeholder="Match AWB to stop" />
                    <button type="submit" disabled={!isRecording}
                      className="bg-red-600 text-white px-3 rounded-r hover:bg-red-700 disabled:opacity-50 transition-colors">
                      <SafeIcon icon={FiIcons.FiSquare} />
                    </button>
                  </div>
                </form>

                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Status</h3>
                    <div className={`text-xs font-bold flex items-center gap-1 ${isRecording ? 'text-red-500' : 'text-green-600'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} />
                      {isRecording ? 'REC' : 'STANDBY'}
                    </div>
                  </div>
                  <div className="text-2xl font-mono font-bold text-gray-800">{formatTime(recordingTime)}</div>
                  {isRecording && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-1000 ${timeWarning ? 'bg-red-500' : 'bg-blue-500'}`}
                          style={{ width: `${(recordingTime / MAX_RECORDING_SECONDS) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">Max 5 min • {formatTime(timeLeft)} remaining</p>
                    </div>
                  )}
                </div>

                <button onClick={toggleCamera} disabled={isRecording}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${cameraOn ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100' : 'border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>
                  <SafeIcon icon={cameraOn ? FiIcons.FiVideo : FiIcons.FiVideoOff} />
                  Camera {cameraOn ? 'ON' : 'OFF'}
                </button>

                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      <SafeIcon icon={FiIcons.FiSettings} className="text-gray-400" /> Storage
                    </span>
                    <span className="flex gap-1.5">
                      {downloadToPC  && <span className="text-xs bg-blue-100   text-blue-700   px-2 py-0.5 rounded-full font-medium">💾 PC</span>}
                      {uploadToCloud && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">☁️ Cloud</span>}
                    </span>
                  </p>
                  <div className="space-y-2">
                    <ToggleSwitch enabled={downloadToPC}  onChange={setDownloadToPC}  label="Download to PC"  description="Save to your computer" color="blue"   icon="💾" />
                    <ToggleSwitch enabled={uploadToCloud} onChange={setUploadToCloud} label="Upload to Cloud" description="Save to cloud storage" color="purple" icon="☁️" />
                  </div>
                  {!downloadToPC && !uploadToCloud && (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg flex items-center gap-2 mt-2">
                      <SafeIcon icon={FiIcons.FiAlertTriangle} /> Enable at least one option.
                    </p>
                  )}
                </div>

                <BrandNameInput value={brandName} onChange={setBrandName} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Floating uploads tray ── */}
      <UploadsTray />
    </div>
  );
};

export default ShipmentRecorder;